// Self-contained confetti + small fireworks on a <canvas>. No npm dependency.
// runConfetti(canvas, durationMs) starts the animation and returns a cancel fn.

interface P { x:number; y:number; vx:number; vy:number; size:number; color:string; rot:number; vrot:number; life:number; max:number; circ:boolean }

const COLORS = ['#ff6a5e', '#ff9f45', '#ffd23f', '#4ea1ff', '#7c5cff', '#ff5da2', '#3ddc84']
const rnd = (a: number, b: number) => a + Math.random() * (b - a)
const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)]
const MAX = 220

export function runConfetti(canvas: HTMLCanvasElement, durationMs = 4000): () => void {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const rect = canvas.getBoundingClientRect()
  canvas.width = Math.max(1, rect.width * dpr)
  canvas.height = Math.max(1, rect.height * dpr)
  const W = canvas.width, H = canvas.height
  const parts: P[] = []

  const cannon = (n: number, ox: number, oy: number, spread: number, speed: number) => {
    for (let i = 0; i < n && parts.length < MAX; i++) {
      const a = rnd(-Math.PI / 2 - spread, -Math.PI / 2 + spread)
      const v = rnd(speed * 0.5, speed)
      parts.push({ x: ox, y: oy, vx: Math.cos(a) * v, vy: Math.sin(a) * v, size: rnd(4, 9) * dpr, color: pick(COLORS), rot: rnd(0, Math.PI), vrot: rnd(-0.2, 0.2), life: 0, max: rnd(70, 130), circ: Math.random() < 0.3 })
    }
  }
  const firework = (x: number, y: number) => {
    const n = 26, hue = pick(COLORS)
    for (let i = 0; i < n && parts.length < MAX; i++) {
      const a = (i / n) * Math.PI * 2, v = rnd(2, 5) * dpr
      parts.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, size: rnd(2, 4) * dpr, color: hue, rot: 0, vrot: 0, life: 0, max: rnd(40, 70), circ: true })
    }
  }

  // initial: two bottom-corner confetti cannons
  cannon(70, W * 0.08, H, 0.5, 14 * dpr)
  cannon(70, W * 0.92, H, 0.5, 14 * dpr)
  // a few small fireworks during the run
  const timers = [600, 1400, 2200].map(t => window.setTimeout(() => firework(W * rnd(0.25, 0.75), H * rnd(0.18, 0.45)), t))

  const start = performance.now()
  let raf = 0
  const tick = (now: number) => {
    const grav = 0.18 * dpr
    ctx.clearRect(0, 0, W, H)
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i]
      p.vy += grav; p.x += p.vx; p.y += p.vy; p.vx *= 0.99; p.rot += p.vrot; p.life++
      const alpha = Math.max(0, 1 - p.life / p.max)
      if (alpha <= 0 || p.y > H + 20) { parts.splice(i, 1); continue }
      ctx.globalAlpha = alpha; ctx.fillStyle = p.color
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot)
      if (p.circ) { ctx.beginPath(); ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2); ctx.fill() }
      else ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6)
      ctx.restore()
    }
    ctx.globalAlpha = 1
    if (now - start > durationMs && parts.length === 0) return // finished — stop the loop
    raf = requestAnimationFrame(tick)
  }
  raf = requestAnimationFrame(tick)

  return () => { cancelAnimationFrame(raf); timers.forEach(clearTimeout); ctx.clearRect(0, 0, W, H) }
}
